import type { FC } from "react";

import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import { Button } from "@jti/core-components/components/Button";
import { Container } from "@jti/core-components/components/Container";
import { useSelector } from "@jti/store/reduxHooks";
import { type RootStoreState } from "@jti/store/types";

import useLoggedUser from "../../hooks/useLoggedUser";
import { trackCtaClick } from "../../utils/trackCtaClick";

import type { ReferralStatsItemProps } from "./ReferralStatsItem";
import ReferralStatsItem from "./ReferralStatsItem";

import styles from "./ReferralStats.module.scss";

export type ReferralStatsProps = {
  title: string;
  referralstatslist: ReferralStatsItemProps[];
  referralhistory: string;
  referralhistorylink: string;
  isInEditor: boolean;
  theme?: "light" | "dark";
};

export const ReferralStats: FC<ReferralStatsProps> = ({
  title,
  referralstatslist,
  referralhistory,
  referralhistorylink,
  isInEditor,
  theme = "light",
}) => {
  const [isLogged] = useLoggedUser();
  const { isReferralOn } = useConfig();
  const { referralStats } = useSelector((state: RootStoreState) => state.loyaltyCore);
  const dispatchGTMEvent = () => {
    trackCtaClick(referralhistory, "ReferralStatsCTA");
  };

  const valueByType = (type: string | number): string | number => {
    switch (type) {
      case "totalReward":
        return referralStats?.earned_points;
      case "createdAccount":
        return referralStats?.referees_count;
      case "completedFirstOrder":
        return referralStats?.completed_first_order_count;
      default:
        return "";
    }
  };

  if (!(isLogged && isReferralOn) && !isInEditor) {
    return null;
  }

  return (
    <Container>
      <div
        className={classNames(styles.block, {
          ["light-theme"]: theme === "light",
          ["dark-theme"]: theme === "dark",
        })}
      >
        <div className={styles.title} data-testid="referralStatsTitle">
          {title}
        </div>
        <div className={styles.referreesBlock} data-testid="referralStatsBlock">
          {referralstatslist.map((item, id) => (
            <ReferralStatsItem
              key={id}
              imageUrl={item.imageUrl}
              description={item.description}
              value={valueByType(item.value)}
              dataTestId={item.value}
            />
          ))}
          <Button
            variant="text-muted"
            data-testid="arrow-link"
            size="sm"
            href={referralhistorylink}
            onClick={dispatchGTMEvent}
            iconAfter={LongArrowForwardIcon}
          >
            {referralhistory}
          </Button>
        </div>
      </div>
    </Container>
  );
};
